import { SxProps, Theme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useClearRefinements, useSearchBox } from 'react-instantsearch';
import ClearAllButton from '../../common/components/ClearAllButton';
import useNormalizedCurrentRefinements from '../../hooks/useNormalizedCurrentRefinements';
import useRefine from '../../hooks/useRefine';
import useDiscoveryDefaultFilters, { SearchMode } from '../../hooks/useDiscoveryDefaultFilters';
import { currentContextAtom } from '../../state';
import FilterUtils from './filter.utils';

interface ClearFiltersButtonProps {
  attributesToCheck: string[];
  excludedAttributes?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultRefinements?: any[];
  searchMode?: SearchMode;
  sx?: SxProps<Theme>;
}

export default function ClearFiltersButton({ attributesToCheck, excludedAttributes, defaultRefinements = [], searchMode, sx }: ClearFiltersButtonProps): React.JSX.Element {
  const { refine: clearRefinements } = useClearRefinements({ excludedAttributes });
  const currentRefinements = useNormalizedCurrentRefinements(attributesToCheck) as string[];
  const { query, clear: clearQuery } = useSearchBox();
  const { refine, uiState } = useRefine();

  const { companyId } = useAtomValue(currentContextAtom);
  const defaultFilters = useDiscoveryDefaultFilters(searchMode);
  const signedContractFilter = FilterUtils.GetSignedContractFilter(searchMode === 'PRODUCT' ? 'buyers' : 'partners', companyId!);
  const defaultContractFiltersValue = `${defaultFilters} AND ${signedContractFilter}`;

  const clearAll = (): void => {
    clearRefinements();
    clearQuery();
    refine({
      aroundRadius: 'all',
      filters: defaultContractFiltersValue,
    });
  };

  const displayButton = useMemo((): boolean => {
    const hasDefaultRefinements = !defaultRefinements.length
      || (defaultRefinements.length === currentRefinements.length && currentRefinements.every((value) => defaultRefinements.includes(value)));

    return currentRefinements.length + query.length > defaultRefinements.length
      || !hasDefaultRefinements
      || Number.isInteger(uiState.aroundRadius)
      || (uiState.filters !== undefined && (defaultContractFiltersValue !== uiState.filters));
  }, [currentRefinements, query, uiState]);

  return displayButton
    ? (
      <ClearAllButton
        sx={{
          height: 40,
          ...sx,
        }}
        onClick={clearAll}
      />
    )
    // eslint-disable-next-line react/jsx-no-useless-fragment
    : <></>;
}
